/**
 * Burnaway Shop - app.js
 * Ported from source: resources/scripts/app.js
 *
 * Behaviors:
 * 1. Dark mode sync (source lines 13-19)
 * 2. Mobile menu toggle (source lines 21-57)
 *
 * Cart count display is owned by Dawn's Section Rendering API.
 * cart-drawer.js getSectionsToRender() includes 'cart-icon-bubble', so the
 * <a id="cart-icon-bubble"> in the header is replaced with fresh server-rendered
 * HTML on every cart update. A custom pubsub.cartUpdate subscriber here would
 * race against that swap and silently break the cart drawer click handler after
 * the first update.
 */

document.addEventListener('DOMContentLoaded', function () {

  // ========== 1. Dark Mode Sync ==========
  // Source: app.js lines 13-19
  // Wrapped in whenDefined to avoid a race with dark-mode-toggle.js (type="module")
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('dark-mode-toggle').then(function () {
      var darkToggle = document.querySelector('dark-mode-toggle');
      if (!darkToggle) return;
      // Sync on load
      document.documentElement.classList.toggle('dark', darkToggle.mode === 'dark');
      // Sync on change
      darkToggle.addEventListener('colorschemechange', function (e) {
        document.documentElement.classList.toggle('dark', e.detail.colorScheme === 'dark');
      });
    });
  }

  // ========== 2. Menu Toggle ==========
  // Source: app.js lines 21-57
  var topNavToggle = document.getElementById('topNavToggle');
  var toggler = document.getElementById('menuToggler');
  var flagWrap = document.querySelector('.flag-wrap');

  if (toggler && topNavToggle) {
    function closeMenu() {
      var searchT = document.getElementById('searchToggler');
      if (searchT) searchT.setAttribute('aria-expanded', 'false');
      topNavToggle.classList.add('hidden');
      if (flagWrap) flagWrap.classList.remove('hidden');
      toggler.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', trapMenuFocus);
    }

    function trapMenuFocus(e) {
      if (e.key === 'Escape') {
        closeMenu();
        toggler.focus();
        return;
      }
      if (e.key !== 'Tab') return;
      // Include form fields (the overlay contains the search input) and drop
      // hidden elements (e.g. the display:none reset button) so first/last are
      // always focusable — otherwise Tab escapes into the page behind the menu.
      var candidates = topNavToggle.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
      var focusable = Array.prototype.filter.call(candidates, function (el) {
        return el.offsetParent !== null;
      });
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    toggler.addEventListener('click', function () {
      var isOpen = !topNavToggle.classList.contains('hidden');
      topNavToggle.classList.toggle('hidden');
      if (flagWrap) flagWrap.classList.toggle('hidden');
      toggler.setAttribute('aria-expanded', String(!isOpen));
      var searchT = document.getElementById('searchToggler');
      if (searchT) searchT.setAttribute('aria-expanded', String(!isOpen));
      if (!isOpen) {
        // Menu just opened — lock scroll, focus first link and trap focus
        document.body.style.overflow = 'hidden';
        var firstLink = topNavToggle.querySelector('a[href]');
        if (firstLink) firstLink.focus();
        document.addEventListener('keydown', trapMenuFocus);
      } else {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', trapMenuFocus);
      }
    });

    // Close on outside click; restore focus to toggler if keyboard user was inside the menu
    document.addEventListener('click', function (e) {
      if (!topNavToggle.classList.contains('hidden') &&
          !topNavToggle.contains(e.target) &&
          !toggler.contains(e.target)) {
        var wasInside = topNavToggle.contains(document.activeElement);
        closeMenu();
        if (wasInside && toggler) toggler.focus();
      }
    });

    // Close menu when any link inside the overlay is activated. Prevents the
    // menu's focus trap from competing with the cart drawer's focus trap when
    // a /cart link is configured in the menu, and removes a brief flash of
    // the open menu during page navigation.
    topNavToggle.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (link && topNavToggle.contains(link)) {
        closeMenu();
      }
    });
  }

});

// Pill dropdown: close on selection (delegated so it survives variant-selects view transitions)
document.addEventListener('change', function (e) {
  if (e.target.matches('.pill-dropdown__panel input[type="radio"]')) {
    var dropdown = e.target.closest('.pill-dropdown');
    if (dropdown) dropdown.removeAttribute('open');
  }
});

// Pill dropdown: close when clicking outside
document.addEventListener('click', function (e) {
  document.querySelectorAll('.pill-dropdown[open]').forEach(function (dropdown) {
    if (!dropdown.contains(e.target)) {
      dropdown.removeAttribute('open');
    }
  });
});

// Description read-more toggle (delegated so it survives product-info.js full-page
// swaps, which replace the description node and would drop a directly-bound listener).
document.addEventListener('click', function (e) {
  var btn = e.target.closest('.product-description__toggle');
  if (!btn) return;
  var container = btn.closest('.product-description');
  if (!container) return;
  var isExpanded = btn.getAttribute('aria-expanded') === 'true';
  container.classList.toggle('product-description--clamped', isExpanded);
  container.classList.toggle('product-description--expanded', !isExpanded);
  btn.setAttribute('aria-expanded', String(!isExpanded));
  btn.textContent = isExpanded ? btn.dataset.moreText : btn.dataset.lessText;
});

// Quick-add: a color-fill-and-hold on add-to-cart, in place of a spinner.
// Decoupled from the cart/add round-trip (which can resolve in ~100ms) so the
// fill always completes; the cart drawer sliding out remains the success cue.
// The 800ms hold matches quick-add-fill in product-card.css. Cleanup is a
// managed timer rather than animationend because the reduced-motion path
// disables the animation, so animationend would never fire there.
document.addEventListener('click', function (e) {
  var btn = e.target.closest('button.quick-add__submit');
  if (!btn) return;
  // product-form.js sets aria-disabled while a cart/add request is in flight
  // and swallows the submit; don't replay the fill for swallowed clicks.
  if (btn.getAttribute('aria-disabled') === 'true') return;
  clearTimeout(btn._fillTimer);
  // A prior failed add may have left the error state pending (is-error class,
  // swapped label, 2500ms restore timer). A re-click that succeeds must clear
  // it now, otherwise the button keeps showing the error label until the stale
  // timer fires after the cart drawer has already opened.
  clearTimeout(btn._errorTimer);
  btn.classList.remove('is-adding', 'is-error');
  if (btn._restingLabel) {
    var addingLabel = btn.querySelector('.quick-add__label');
    if (addingLabel) addingLabel.textContent = btn._restingLabel;
    btn._restingLabel = null;
  }
  void btn.offsetWidth; // restart the animation on rapid re-clicks
  btn.classList.add('is-adding');
  btn._fillTimer = setTimeout(function () { btn.classList.remove('is-adding'); }, 800);
});

// Quick-add failure: the fill above is optimistic, so when cart/add is refused
// (e.g. remaining inventory already in the cart) the completed fill would read
// as success. product-form.js publishes cartError with the variant id; the
// quick-add forms have no inline error wrapper, so surface the failure on the
// button and announce it via #QuickAddLiveRegion (layout/theme.liquid).
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartError, function (event) {
    if (!event || event.source !== 'product-form') return;
    document.querySelectorAll('product-form.quick-add .product-variant-id').forEach(function (input) {
      if (String(input.value) !== String(event.productVariantId)) return;
      var btn = input.form && input.form.querySelector('button.quick-add__submit');
      if (!btn) return;
      // product-form.js sets aria-disabled on the submitted form's button for the
      // duration of the cart/add request and clears it in .finally() (which runs
      // after this subscriber). Only the form the user actually clicked is disabled
      // now, so skip other cards that merely share the same variant id — otherwise
      // one refused add flips every duplicate card to the error state.
      if (btn.getAttribute('aria-disabled') !== 'true') return;
      clearTimeout(btn._fillTimer);
      clearTimeout(btn._errorTimer);
      btn.classList.remove('is-adding');
      btn.classList.add('is-error');
      var label = btn.querySelector('.quick-add__label');
      if (label && !btn._restingLabel) btn._restingLabel = label.textContent;
      if (label && btn.dataset.errorLabel) label.textContent = btn.dataset.errorLabel;
      var live = document.getElementById('QuickAddLiveRegion');
      if (live) {
        var message = typeof event.errors === 'string' && event.errors
          ? event.errors
          : btn.dataset.errorLabel || '';
        // Clear then set on the next frame so a repeated identical message
        // still registers as a change and is re-announced by the polite region.
        live.textContent = '';
        requestAnimationFrame(function () { live.textContent = message; });
      }
      btn._errorTimer = setTimeout(function () {
        btn.classList.remove('is-error');
        if (label && btn._restingLabel) label.textContent = btn._restingLabel;
      }, 2500);
    });
  });
}
